import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Percent } from "lucide-react";

export default function PricingPage() {
  const { token } = useAuth();
  const [tables, setTables] = useState([]);
  const [promos, setPromos] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tableModal, setTableModal] = useState(false);
  const [promoModal, setPromoModal] = useState(false);
  const [tableForm, setTableForm] = useState({ name: "", description: "" });
  const [tableItems, setTableItems] = useState([{ product_id: "", price: "" }]);
  const EMPTY_PROMO = { name: "", discount_type: "percent", discount_value: "", start_date: "", end_date: "", applies_to: "all", product_ids: [], category_ids: [] };
  const [promoForm, setPromoForm] = useState(EMPTY_PROMO);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [t, p, pr, c] = await Promise.all([
        axios.get(`${API_URL}/price-tables`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/promotions`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/categories`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTables(t.data); setPromos(p.data); setProducts(pr.data); setCategories(c.data);
    } catch { toast.error("Erro ao carregar preços"); }
  };

  const createTable = async (e) => {
    e.preventDefault();
    const items = tableItems.filter((i) => i.product_id && i.price).map((i) => ({ product_id: i.product_id, price: toBaseCurrency(i.price) }));
    try {
      await axios.post(`${API_URL}/price-tables`, { ...tableForm, items }, { headers, withCredentials: true });
      toast.success("Tabela de preços criada");
      setTableModal(false); setTableForm({ name: "", description: "" }); setTableItems([{ product_id: "", price: "" }]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteTable = async (t) => {
    if (!window.confirm(`Remover tabela "${t.name}"?`)) return;
    await axios.delete(`${API_URL}/price-tables/${t.price_table_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const createPromo = async (e) => {
    e.preventDefault();
    if (promoForm.applies_to === "products" && promoForm.product_ids.length === 0) return toast.error("Selecione pelo menos um artigo");
    if (promoForm.applies_to === "categories" && promoForm.category_ids.length === 0) return toast.error("Selecione pelo menos uma categoria");
    try {
      await axios.post(`${API_URL}/promotions`, {
        name: promoForm.name,
        discount_type: promoForm.discount_type,
        discount_value: promoForm.discount_type === "percent" ? parseFloat(promoForm.discount_value) || 0 : toBaseCurrency(promoForm.discount_value),
        start_date: promoForm.start_date || null,
        end_date: promoForm.end_date || null,
        product_ids: promoForm.applies_to === "products" ? promoForm.product_ids : [],
        category_ids: promoForm.applies_to === "categories" ? promoForm.category_ids : [],
      }, { headers, withCredentials: true });
      toast.success("Promoção criada — já aplicada automaticamente no POS");
      setPromoModal(false); setPromoForm(EMPTY_PROMO);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toggleId = (field, id) =>
    setPromoForm((f) => ({ ...f, [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id] }));

  const promoScope = (p) => {
    if (p.product_ids?.length) return `${p.product_ids.length} artigo(s): ${p.product_ids.map((id) => products.find((x) => x.product_id === id)?.name || "?").slice(0, 3).join(", ")}${p.product_ids.length > 3 ? "…" : ""}`;
    if (p.category_ids?.length) return `Categorias: ${p.category_ids.map((id) => categories.find((x) => x.category_id === id)?.name || "?").join(", ")}`;
    return "Todos os artigos";
  };

  const togglePromo = async (p) => {
    await axios.put(`${API_URL}/promotions/${p.promotion_id}/toggle`, {}, { headers, withCredentials: true });
    fetchAll();
  };

  const deletePromo = async (p) => {
    if (!window.confirm(`Remover promoção "${p.name}"?`)) return;
    await axios.delete(`${API_URL}/promotions/${p.promotion_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Preços & Promoções</h1>
        <p className="text-muted-foreground">Tabelas de preços e campanhas de desconto</p>
      </div>

      <Tabs defaultValue="tables">
        <TabsList>
          <TabsTrigger value="tables" data-testid="pricing-tab-tables">Tabelas de Preços</TabsTrigger>
          <TabsTrigger value="promos" data-testid="pricing-tab-promos">Promoções</TabsTrigger>
        </TabsList>

        <TabsContent value="tables" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setTableModal(true)} data-testid="add-price-table-btn"><Plus className="w-4 h-4 mr-2" /> Nova Tabela</Button>
          </div>
          {tables.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Tag className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma tabela de preços</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {tables.map((t) => (
                <Card key={t.price_table_id} data-testid={`price-table-${t.price_table_id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{t.name}</p>
                        {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteTable(t)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {(t.items || []).map((it, i) => (
                        <div key={i} className="flex justify-between text-sm border-b pb-1">
                          <span>{it.product_name}</span>
                          <span className="font-mono font-medium">{formatCurrency(it.price)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="promos" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setPromoModal(true)} data-testid="add-promo-btn"><Plus className="w-4 h-4 mr-2" /> Nova Promoção</Button>
          </div>
          <div className="space-y-2">
            {promos.map((p) => (
              <Card key={p.promotion_id} data-testid={`promo-${p.promotion_id}`}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10"><Percent className="w-4 h-4 text-primary" /></div>
                    <div>
                      <p className="font-semibold">{p.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.discount_type === "percent" ? `${p.discount_value}% desconto` : `${formatCurrency(p.discount_value)} desconto`}
                        {p.start_date && ` · de ${p.start_date}`}{p.end_date && ` até ${p.end_date}`}
                      </p>
                      <p className="text-xs text-primary/80" data-testid={`promo-scope-${p.promotion_id}`}>{promoScope(p)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={p.active ? "bg-emerald-500/20 text-emerald-600" : "bg-slate-500/20 text-slate-500"}>{p.active ? "Ativa" : "Inativa"}</Badge>
                    <Switch checked={p.active} onCheckedChange={() => togglePromo(p)} data-testid={`toggle-promo-${p.promotion_id}`} />
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePromo(p)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {promos.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Nenhuma promoção registada</CardContent></Card>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={tableModal} onOpenChange={setTableModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nova Tabela de Preços</DialogTitle></DialogHeader>
          <form onSubmit={createTable} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={tableForm.name} onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })} placeholder="Ex: Preços VIP, Happy Hour" data-testid="price-table-name-input" /></div>
            <div className="space-y-1"><Label>Descrição</Label><Input value={tableForm.description} onChange={(e) => setTableForm({ ...tableForm, description: e.target.value })} /></div>
            <Label>Produtos e Preços</Label>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {tableItems.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <Select value={it.product_id} onValueChange={(v) => { const arr = [...tableItems]; arr[i].product_id = v; setTableItems(arr); }}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Produto" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" min="0" className="w-28" placeholder="Preço" value={it.price} onChange={(e) => { const arr = [...tableItems]; arr[i].price = e.target.value; setTableItems(arr); }} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setTableItems(tableItems.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => setTableItems([...tableItems, { product_id: "", price: "" }])}><Plus className="w-4 h-4 mr-1" /> Adicionar Produto</Button>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTableModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-price-table-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={promoModal} onOpenChange={setPromoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Promoção</DialogTitle>
            <DialogDescription>Promoções ativas são aplicadas automaticamente no POS e na fatura.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createPromo} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={promoForm.name} onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })} data-testid="promo-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={promoForm.discount_type} onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}>
                  <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentual (%)</SelectItem>
                    <SelectItem value="value">Valor Fixo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Valor *</Label><Input required type="number" step="0.01" min="0" value={promoForm.discount_value} onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })} data-testid="promo-value-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Início</Label><Input type="date" value={promoForm.start_date} onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value })} data-testid="promo-start-input" /></div>
              <div className="space-y-1"><Label>Fim</Label><Input type="date" value={promoForm.end_date} onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })} data-testid="promo-end-input" /></div>
            </div>
            <div className="space-y-1">
              <Label>Aplica-se a</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {[["all", "Todos os artigos"], ["products", "Artigos"], ["categories", "Categorias"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPromoForm({ ...promoForm, applies_to: v })} className={`h-8 rounded-md border text-xs font-medium transition-colors ${promoForm.applies_to === v ? "bg-primary text-primary-foreground border-primary" : "bg-muted hover:border-primary/50"}`} data-testid={`promo-scope-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            {promoForm.applies_to === "products" && (
              <div className="max-h-44 overflow-y-auto rounded-md border p-2 space-y-1" data-testid="promo-products-list">
                {products.map((p) => (
                  <label key={p.product_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/60 rounded px-1 py-0.5">
                    <input type="checkbox" checked={promoForm.product_ids.includes(p.product_id)} onChange={() => toggleId("product_ids", p.product_id)} data-testid={`promo-product-${p.product_id}`} />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{formatCurrency(p.price)}</span>
                  </label>
                ))}
              </div>
            )}
            {promoForm.applies_to === "categories" && (
              <div className="max-h-44 overflow-y-auto rounded-md border p-2 space-y-1" data-testid="promo-categories-list">
                {categories.map((c) => (
                  <label key={c.category_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/60 rounded px-1 py-0.5">
                    <input type="checkbox" checked={promoForm.category_ids.includes(c.category_id)} onChange={() => toggleId("category_ids", c.category_id)} data-testid={`promo-category-${c.category_id}`} />
                    <span className="flex-1 truncate">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPromoModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-promo-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
